import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import LuxButton from "../components/landing/LuxButton";
import { API } from "../App";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/email/password-reset-request`, { email });
      setSubmitted(true);
      toast.success("If an account exists, a reset link has been sent");
    } catch {
      // Don't reveal whether the email exists
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lux">
      <div className="lux-auth-shell">
        <div className="lux-auth-card" data-testid="forgot-password-card">
          <Link to="/" className="flex items-center justify-center mb-7">
            <img src="/logo.png" alt="Fieldz AI" className="h-11 object-contain" />
          </Link>

          {!submitted ? (
            <>
              <div className="text-center mb-7">
                <h1 className="text-[26px] font-bold text-[var(--lux-text)] mb-1.5" style={{ letterSpacing: "-0.02em" }}>
                  Forgot password?
                </h1>
                <p className="lux-body text-[14px]">Enter your email and we'll send you a reset link.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="lux-label">Email Address</label>
                  <div className="relative">
                    <Mail className="lux-input-icon" />
                    <input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="lux-input has-icon"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <LuxButton type="submit" variant="primary" size="lg" className="w-full" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? "Sending..." : "Send Reset Link"}
                </LuxButton>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: "rgba(34, 197, 94, 0.15)", border: "1px solid rgba(34, 197, 94, 0.30)" }}
              >
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-[24px] font-bold text-[var(--lux-text)] mb-2" style={{ letterSpacing: "-0.02em" }}>
                Check your email
              </h1>
              <p className="lux-body text-[14px] mb-6">
                If an account exists for <span className="text-[var(--lux-text)]">{email}</span>, we've sent password reset instructions.
              </p>
              <p className="text-[13px] mb-6" style={{ color: "var(--lux-text-3)" }}>
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <LuxButton
                variant="quiet"
                className="w-full"
                onClick={() => setSubmitted(false)}
                data-testid="forgot-try-again-btn"
              >
                Try Again
              </LuxButton>
            </div>
          )}

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 text-[14px] mt-7 transition-colors"
            style={{ color: "var(--lux-text-2)" }}
            data-testid="forgot-back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
